import { CSSProperties, ReactNode } from "react";
import { useKeyboardHeight } from "../../hooks/use-keyboard-height";

interface KeyboardAvoidingViewProps {
  children: ReactNode;
  /**
   * Behavior when keyboard appears
   * - "padding": Adds padding-bottom equal to keyboard height
   * - "position": Adjusts bottom position (useful for fixed positioned elements)
   * - "height": Adjusts height by subtracting keyboard height
   */
  behavior?: "padding" | "position" | "height";
  /**
   * CSS length added to the keyboard height, e.g. a negative inset for the
   * space a host already leaves between this view and the screen bottom
   * @default "0px"
   */
  offset?: string;
  /**
   * Whether to enable keyboard avoidance
   * @default true
   */
  enabled?: boolean;
  /**
   * Additional className
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: CSSProperties;
  /**
   * Transition duration for style changes (in seconds)
   * @default 0.22
   */
  transitionDuration?: number;
}

/**
 * Web version of React Native's KeyboardAvoidingView
 * Adjusts layout when keyboard appears using Visual Viewport API
 */
export default function KeyboardAvoidingView({
  children,
  behavior = "padding",
  offset = "0px",
  enabled = true,
  className,
  style,
  transitionDuration = 0.22,
}: KeyboardAvoidingViewProps) {
  const { keyboardHeight, isKeyboardOpen } = useKeyboardHeight();

  const computedStyle: CSSProperties = {
    ...style,
    transition: `all ${transitionDuration}s ease-out`,
  };

  if (enabled && isKeyboardOpen && keyboardHeight > 0) {
    const adjustedHeight = `calc(${keyboardHeight}px + ${offset})`;

    switch (behavior) {
      case "padding": {
        computedStyle.paddingBottom = adjustedHeight;
        break;
      }
      case "position": {
        computedStyle.bottom = adjustedHeight;
        break;
      }
      case "height": {
        computedStyle.height = `calc(100% - ${adjustedHeight})`;
        break;
      }
    }
  }

  return (
    <div className={className} style={computedStyle}>
      {children}
    </div>
  );
}
